import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Copy, Check, Linkedin, Film, Upload, Loader2 } from "lucide-react";
import { LegalHeader, LegalFooter } from "./legal/Terms";
import useSEO from "../lib/useSEO";

const POSTERS = [
  {
    slug: "copilot",
    title: "NAVBOT — Executive AI",
    tone: "#7A1F2B",
    caption: `Your CFO doesn't need another dashboard. They need an executive AI that speaks board English.

Capittance Harmonizer ships with NAVBOT — Claude Sonnet 4.5, trained on your live portfolio data, activated the moment your C-Suite subscription goes live. Available only to paying C-Suite tenants.

Three questions it answers really well:
→ "Summarise this quarter's RAID log for a CFO audience."
→ "Compare S/4HANA vs D365 F&O for a mid-cap consumer business."
→ "Draft a value-realisation story from these initiatives."

$200/mo Starter · $350/mo Platinum · 14-day pilot at $0.
capittance.com/?utm_source=linkedin&utm_campaign=li-copilot

#SAP #Dynamics365 #CIO #CFO #ProgrammeDelivery #AI #ClaudeAI #Boardroom`,
  },
  {
    slug: "boardpack",
    title: "One-Click Board Pack",
    tone: "#064E60",
    caption: `20 slides. 60 seconds. Zero Sunday-night deck rebuilds.

Capittance Harmonizer's Board Pack Generator pulls your live portfolio KPIs, RAID summary, budget burn, value realisation and decisions requested into a boardroom-ready 20-slide PPT in one click.

And when you upload your company template ($50 one-time), every slide auto-inherits your brand colours, typography and letterhead. No more design bottleneck.

Every SAP/D365 CIO needs this Friday afternoon.

capittance.com/?utm_source=linkedin&utm_campaign=li-boardpack

#BoardPack #CIO #CFO #SAP #Dynamics365 #Delivery #PMO`,
  },
  {
    slug: "pilot",
    title: "14-Day Platinum Pilot",
    tone: "#B45309",
    caption: `$0 today. Full Platinum access. Cancel any time.

We know the CIO buying cycle. Contracts, procurement, DPA reviews — you don't have time for a sales gauntlet.

So Capittance Harmonizer Platinum ($350/mo) now starts with a 14-day pilot at $0. Card up-front, no charge until day 15, one-click cancel from your dashboard. Zero friction.

If it's the right tool for your programme, you'll know in a week. If it isn't, downgrade to free Starter and keep every RAID, project and export you built during the pilot.

capittance.com/pricing?utm_source=linkedin&utm_campaign=li-pilot

#SAP #Dynamics365 #CIO #ExecutiveEval #Platinum`,
  },
  {
    slug: "cockpits",
    title: "Live SAP + D365 Cockpits",
    tone: "#0369A1",
    caption: `Watch your S/4HANA and D365 programme breathe in real time.

Capittance Harmonizer wires straight into your tenants:
→ SAP S/4HANA via OData (basic auth or OAuth)
→ Dynamics 365 F&O via Azure AD app credentials
→ 134-step SAP Activate readiness view
→ Live Revenue · Pipeline · EBITDA · Cash tiles

Secrets stay server-side. No Excel exports. No ETL. No overnight refresh. Just your delivery, live.

capittance.com/?utm_source=linkedin&utm_campaign=li-cockpits

#SAP #S4HANA #Dynamics365 #CIO #CFO #Delivery #SAPActivate #FastTrack`,
  },
  {
    slug: "referral",
    title: "Referral Rewards",
    tone: "#047857",
    caption: `Refer a friend. Both get a free month.

Capittance Harmonizer's referral programme is stupidly simple:
1. Grab your link from the dashboard
2. Share it with a CIO or delivery director
3. When they subscribe, both of you get a Stripe account credit equal to one month of your plan ($200 Starter or $350 Platinum)
4. Applied automatically to your next invoice — no coupon codes to enter, no admin

No cap on friends. Up to 3 free months per year.

Try it: capittance.com/signup?ref=YOUR-CODE

#SAP #Dynamics365 #Referral #CIO #DeliveryLeader`,
  },
];

export default function LinkedInKit() {
  useSEO({
    title: "LinkedIn marketing kit · Capittance Harmonizer",
    description: "Five ready-to-post LinkedIn images + captions for Capittance Harmonizer. Free to download, copy and share.",
    path: "/linkedin",
  });
  return (
    <div className="min-h-screen bg-white">
      <LegalHeader title="LinkedIn Kit" subtitle="5 posters + captions · ready to post" />
      <main className="max-w-4xl mx-auto px-6 py-10" data-testid="page-linkedin">
        <div className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Free · Bookmark this page</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-zinc-900 leading-tight mt-2">Post one a week for five weeks.</h1>
          <p className="text-base text-zinc-700 leading-relaxed mt-3 max-w-2xl">
            Five colourful LinkedIn feed images + ready-to-post captions. Each highlights a different Capittance Harmonizer perk. Download the image, copy the caption, tag whoever&rsquo;s in your ICP, done.
          </p>
        </div>

        <div className="space-y-8">
          {POSTERS.map((p, i) => <PosterCard key={p.slug} idx={i} {...p}/>)}
        </div>

        <CoBrandForm />

        <div className="card p-6 mt-10 text-center" style={{ background: "linear-gradient(135deg,#EEF2FF,#DDD6FE)" }}>
          <Linkedin size={22} className="mx-auto text-indigo-700 mb-2"/>
          <h3 className="font-display text-xl font-extrabold tracking-tight">Want a custom template with your logo?</h3>
          <p className="text-sm text-zinc-700 mt-1 max-w-lg mx-auto">Email <a href="mailto:Requests@Capittance.com?subject=Custom%20LinkedIn%20template" className="underline">Requests@Capittance.com</a> and we&rsquo;ll drop your logo into the poster art and send you the PNG within a day.</p>
        </div>

        <LegalFooter />
      </main>
    </div>
  );
}

function PosterCard({ slug, title, caption, tone, idx }) {
  const [copied, setCopied] = useState(false);
  const src = `/linkedin/li-${slug}.png`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(caption); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* noop */ }
  };
  return (
    <div className="card p-4 md:p-5 grid md:grid-cols-2 gap-5" data-testid={`li-poster-${slug}`}>
      <a href={src} download={`capittance-linkedin-${slug}.png`} className="block">
        <img src={src} alt={title} className="w-full h-auto rounded-sm border border-[var(--border)] shadow-sm hover:shadow-md transition-shadow" data-testid={`li-img-${slug}`}/>
      </a>
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: tone }}>Poster {idx + 1} · 1200×1200</div>
        <h2 className="font-display text-xl font-extrabold tracking-tight text-zinc-900 mt-0.5">{title}</h2>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <a href={src} download={`capittance-linkedin-${slug}.png`} className="btn btn-primary btn-sm inline-flex items-center gap-1.5" data-testid={`li-download-${slug}`}>
            <Download size={13}/> PNG
          </a>
          <a href={`/linkedin/li-${slug}.mp4`} download={`capittance-reel-${slug}.mp4`} className="btn btn-secondary btn-sm inline-flex items-center gap-1.5" data-testid={`li-video-${slug}`}>
            <Film size={13}/> 10s MP4 Reel
          </a>
          <button onClick={copy} className={`btn btn-sm inline-flex items-center gap-1.5 btn-secondary`} data-testid={`li-copy-${slug}`}>
            {copied ? <><Check size={13}/> Copied</> : <><Copy size={13}/> Caption</>}
          </button>
        </div>
        <pre className="mt-3 text-xs whitespace-pre-wrap bg-zinc-50 border border-[var(--border)] rounded-sm p-3 leading-relaxed max-h-72 overflow-y-auto font-sans text-zinc-800" data-testid={`li-caption-${slug}`}>{caption}</pre>
      </div>
    </div>
  );
}

// ─── Co-brand form ────────────────────────────────────────────────────────
function CoBrandForm() {
  const [slug, setSlug] = useState("copilot");
  const [partner, setPartner] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setPreviewUrl(null);
    const f = fileRef.current?.files?.[0];
    if (!f) { setErr("Choose a logo file (PNG or JPG)"); return; }
    if (f.size > 4 * 1024 * 1024) { setErr("Logo must be ≤ 4 MB"); return; }
    setBusy(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const fd = new FormData();
      fd.append("slug", slug);
      fd.append("partner_name", partner);
      fd.append("file", f);
      const r = await fetch(`${base}/api/linkedin/cobrand`, { method: "POST", body: fd });
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      // Also trigger a download automatically
      const a = document.createElement("a");
      a.href = url; a.download = `capittance-${slug}-cobranded.png`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch (ex) { setErr(ex.message || "Co-brand failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="card p-6 mt-10" style={{ background: "linear-gradient(135deg,#FFFBEB,#FEF3C7)" }} data-testid="li-cobrand">
      <div className="font-mono text-[10px] uppercase tracking-widest text-amber-800">Outbound campaigns</div>
      <h2 className="font-display text-2xl font-extrabold tracking-tight text-zinc-900 mt-1">Co-brand a poster with a partner logo</h2>
      <p className="text-sm text-zinc-700 leading-relaxed mt-2 max-w-2xl">
        Upload any square logo (PNG or JPG, ≤4 MB) and we&rsquo;ll drop it into a white plate at the bottom-right of the chosen poster with a <b>PARTNER × CAPITTANCE</b> caption. Free, no watermark, instant download.
      </p>
      <form onSubmit={submit} className="mt-4 grid md:grid-cols-4 gap-3">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-widest text-amber-800">Poster</span>
          <select value={slug} onChange={e => setSlug(e.target.value)} className="mt-1 w-full border border-amber-200 rounded-sm py-2 px-3 text-sm bg-white" data-testid="li-cobrand-slug">
            {POSTERS.map(p => <option key={p.slug} value={p.slug}>{p.title}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-widest text-amber-800">Partner name</span>
          <input value={partner} onChange={e => setPartner(e.target.value)} maxLength={30} placeholder="e.g. ACME CORP"
                 className="mt-1 w-full border border-amber-200 rounded-sm py-2 px-3 text-sm bg-white uppercase" data-testid="li-cobrand-partner"/>
        </label>
        <label className="block md:col-span-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-amber-800">Logo (PNG or JPG)</span>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg" required
                 className="mt-1 w-full border border-amber-200 rounded-sm py-2 px-3 text-sm bg-white" data-testid="li-cobrand-file"/>
        </label>
        <div className="md:col-span-4 flex items-center gap-2">
          <button type="submit" disabled={busy} className="btn btn-primary btn-sm inline-flex items-center gap-1.5" data-testid="li-cobrand-submit">
            {busy ? <><Loader2 size={13} className="animate-spin"/> Compositing…</> : <><Upload size={13}/> Generate co-branded PNG</>}
          </button>
          {err && <span className="text-xs text-red-700" data-testid="li-cobrand-err">{err}</span>}
          {previewUrl && <span className="text-xs text-emerald-700" data-testid="li-cobrand-ok"><Check size={12}/> Downloaded — preview below</span>}
        </div>
      </form>
      {previewUrl && (
        <img src={previewUrl} alt="Co-branded preview" className="mt-4 w-full max-w-md rounded-sm border border-amber-200" data-testid="li-cobrand-preview"/>
      )}
    </div>
  );
}

